import React from 'react';
import { useBranding, useLogoVariant, LogoSlotKey, LogoVariant, FixedLogoVariant } from '@/lib/branding';
import { useTheme } from '@/lib/theme';

/**
 * Brand mark — renders the active preset's approved lockup.
 *
 * How it resolves:
 *   1. If the *active* preset has an uploaded image for the requested
 *      variant (``logo_variants.primary`` etc.) → render that.
 *   2. Otherwise, if the active preset is the built-in Raptor kit → use
 *      the shipped Raptor PNG lockups.
 *   3. Otherwise → render a graceful text-only lockup using the preset's
 *      ``wordmark_text`` / ``wordmark_tagline`` so the surface never
 *      shows the wrong brand while an operator is still uploading logos.
 *
 * Callers can either pin a variant explicitly via the ``variant`` prop
 * (used by the settings preview + marketing lockups) or, more commonly,
 * pass a ``slot`` name so the component honours the operator's branding
 * assignment for that placement.
 */
type Mode = 'default' | 'white' | 'navy';

const RAPTOR_SRC: Record<Mode, string> = {
  default: '/brand/raptor-symbol-primary.png',
  white: '/brand/raptor-symbol-white.png',
  navy: '/brand/raptor-symbol-dark.png',
};

const VARIANT_TO_MODE: Record<FixedLogoVariant, Mode> = {
  primary: 'default',
  mono_white: 'white',
  mono_navy: 'navy',
  mark: 'default',
};

interface BrandLogoProps {
  size?: number;
  monochrome?: 'white' | 'navy';
  showTagline?: boolean;
  markOnly?: boolean;
  /** Force primary gradient regardless of monochrome context. */
  primary?: boolean;
  /** Bind the logo to a branding slot so the assignment picks the variant. */
  slot?: LogoSlotKey;
  /** Explicit variant override (used by the settings preview). */
  variant?: LogoVariant;
  /** Override which preset to preview from (used by the Branding Logos tab). */
  presetOverride?: {
    preset_id?: string;
    logo_variants?: Record<string, string | undefined>;
    wordmark_text?: string;
    wordmark_tagline?: string;
    wordmark_font_family?: string;
    tagline_font_family?: string;
  };
}

export const BrandLogo: React.FC<BrandLogoProps> = ({
  size = 36, monochrome, showTagline = true, markOnly, primary, slot, variant, presetOverride,
}) => {
  const { branding } = useBranding();
  const { resolved } = useTheme();
  const assigned = useLogoVariant((slot ?? 'sidebar_logo') as LogoSlotKey);
  const baseVariant: LogoVariant = variant ?? (slot ? assigned : 'primary');
  // In dark theme, slot-bound logos sit on dark chrome — prefer the white
  // lockup. Explicit variant / preview / monochrome callers are untouched.
  const themeForcesWhite = resolved === 'dark' && !variant && !presetOverride && !primary && !monochrome;
  const effectiveVariant: LogoVariant = themeForcesWhite ? 'mono_white' : baseVariant;

  let mode: Mode;
  if (primary) mode = 'default';
  else if (monochrome) mode = monochrome;
  else if (themeForcesWhite) mode = 'white';
  else mode = VARIANT_TO_MODE[effectiveVariant as FixedLogoVariant] ?? 'default';
  // Only the caller can request an image-only render. The `mark` variant
  // just tells us which lockup file to use; the wordmark text should still
  // sit next to it in a sidebar/header. Callers that truly want no text
  // (favicon, background watermark) pass `markOnly` explicitly.
  const showMarkOnly = markOnly === true;

  // 1. Uploaded logo on the active preset (or the preview preset) wins.
  const previewLogoVariants = presetOverride?.logo_variants;
  const uploadedFromOverride = previewLogoVariants?.[effectiveVariant];
  const uploadedFromActive = (branding as { logo_variants?: Record<string, string> }).logo_variants?.[effectiveVariant];
  const uploadedSrc = uploadedFromOverride || uploadedFromActive;

  // 2. Raptor PNG fallback — only when the active preset (or override) is Raptor.
  const previewPresetId = presetOverride?.preset_id;
  const activePresetId = branding.preset_id || 'raptor';
  const usingRaptor = (previewPresetId ?? activePresetId) === 'raptor';
  const raptorSrc = usingRaptor ? RAPTOR_SRC[mode] : undefined;

  // Wordmark: empty means the operator has intentionally suppressed the
  // text lockup — we honour that by rendering the mark alone (centered,
  // enlarged) rather than falling back to the preset id.
  const rawWordmarkText = presetOverride?.wordmark_text ?? (branding as { wordmark_text?: string }).wordmark_text;
  const rawTagline = presetOverride?.wordmark_tagline ?? (branding as { wordmark_tagline?: string }).wordmark_tagline;
  const wordmarkText = (rawWordmarkText && rawWordmarkText.trim()) ? rawWordmarkText.trim() : '';
  const wordmarkTagline = (rawTagline && rawTagline.trim()) ? rawTagline.trim() : '';
  // Operator-selected wordmark font. Falls back to the preset's display
  // font (which is already wired into --brand-font-display).
  const wordmarkFontFamily = (
    presetOverride?.wordmark_font_family
    ?? (branding as { wordmark_font_family?: string }).wordmark_font_family
    ?? ''
  ).trim() || 'var(--brand-font-display, "Goldman", "Barlow", sans-serif)';
  // Same for the tagline — falls back to the preset's body font.
  const taglineFontFamily = (
    presetOverride?.tagline_font_family
    ?? (branding as { tagline_font_family?: string }).tagline_font_family
    ?? ''
  ).trim() || 'var(--brand-font-body, "Nunito", sans-serif)';

  // If the wordmark text is blank the mark takes over the whole slot —
  // scale it up ~1.15× so it visually fills the space the wordmark
  // would have taken *without* touching the container edges. 1.15 gives
  // a 48 px sidebar mark a comfortable ~55 px inside an h-16 chrome row.
  const noWordmark = !wordmarkText;
  const effectiveMarkOnly = showMarkOnly || noWordmark;
  const markSize = effectiveMarkOnly && !showMarkOnly ? Math.round(size * 1.15) : size;

  const wordFill = mode === 'white' ? '#FFFFFF' : 'var(--raptor-navy, #061935)';
  const taglineFill = mode === 'white' ? 'rgba(255,255,255,0.85)' : 'rgba(6,25,53,0.65)';

  // Resolve the mark image — uploaded > Raptor PNG > graceful initial fallback.
  const src = uploadedSrc || raptorSrc;
  const mark = src ? (
    // Auto-fit: pin the height to the resolved mark size but let the
    // width flow naturally (up to ~3×) so wide horizontal lockups (e.g. the
    // full "OSPREY" wordmark PNG) render proportionally instead of being
    // squashed into a square. A soft drop shadow lifts light-mode marks
    // off white surfaces without competing with the brand palette.
    <img
      src={src}
      alt={wordmarkText || 'Brand mark'}
      style={{
        height: markSize,
        width: 'auto',
        maxWidth: markSize * 3.2,
        maxHeight: markSize,
        objectFit: 'contain',
        display: 'block',
        // Subtle shadow — invisible on dark surfaces because it's translucent
        // navy; on white it lends the mark a gentle sense of depth.
        filter: mode === 'white'
          ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))'
          : 'drop-shadow(0 1px 1px rgba(6,25,53,0.10))',
        // Crisper edges when the browser has to scale bitmap logos up.
        imageRendering: 'auto',
      }}
    />
  ) : (
    // Text-only initial placeholder for presets without uploaded logos.
    // Uses the preset's navy so it stays on-brand while operator uploads.
    // Uses the preset id for the letter (Osprey → "O", Raptor → "R").
    <div
      aria-label="Brand placeholder mark"
      style={{
        width: markSize, height: markSize,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: Math.round(markSize * 0.22),
        background: mode === 'white'
          ? 'rgba(255,255,255,0.12)'
          : 'linear-gradient(135deg, var(--raptor-navy, #061935) 0%, var(--raptor-blue, #0D63FF) 100%)',
        color: '#FFFFFF',
        fontFamily: 'var(--brand-font-display, "Goldman", sans-serif)',
        fontWeight: 700,
        fontSize: Math.max(12, Math.round(markSize * 0.52)),
        letterSpacing: '0.02em',
        lineHeight: 1,
        boxShadow: mode === 'white'
          ? 'inset 0 0 0 1px rgba(255,255,255,0.15)'
          : '0 6px 16px -6px rgba(6,25,53,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
      }}
    >
      {(wordmarkText || (previewPresetId ?? activePresetId) || 'B').slice(0, 1).toUpperCase()}
    </div>
  );

  // Clamp the wordmark so growing the mark for the sidebar doesn't blow
  // the "OSPREY" text past a legible ~20px cap.
  const wordSize = Math.min(size * 0.44, 20);
  const taglineSize = Math.min(Math.max(8, size * 0.18), 11);

  // Mark-alone rendering: wrap in an inline centered flex box that only
  // occupies the space the mark actually needs — this prevents the mark
  // from stretching to fill an entire flex-col parent (as happens on
  // the login page). If a caller wants the mark to horizontally centre
  // inside its own row, they can add `justify-center` to that row.
  if (effectiveMarkOnly) {
    return (
      <div className="inline-flex items-center justify-center" style={{ minHeight: markSize, minWidth: markSize }}>
        {mark}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {mark}
      <div className="leading-tight">
        <div
          className="font-bold"
          style={{ fontSize: wordSize, color: wordFill, letterSpacing: '0.14em', fontFamily: wordmarkFontFamily }}
        >
          {wordmarkText}
        </div>
        {showTagline && wordmarkTagline && (
          <div
            style={{
              fontSize: taglineSize,
              color: taglineFill,
              fontFamily: taglineFontFamily,
              letterSpacing: '0.09em',
            }}
          >
            {wordmarkTagline}
          </div>
        )}
      </div>
    </div>
  );
};

// Backwards-compat alias — the rest of the app still imports RaptorLogo.
export const RaptorLogo = BrandLogo;
